import React, { useContext, useEffect, useRef } from 'react';
import { Actions, ContentTrackerProps, ExternalPayload } from './types';
import { FullScreenTrackingContext } from './FullScreenTrackingContext';
import { WindowFocusContext } from './WindowFocusContext';
import { TrackEvent } from '../../../enums/Content/TrackEvent';
import FullScreenUrlViewer, {
  FullScreenUrlViewerProps,
} from '../../ContentUrl/FullScreenUrlViewer';
import SimpleTracker from '../../../Services/Analytics/SimpleTracker';
import TrackInterface from '../../../graphql/Rooms/TrackInterface';
import ChildSessionInterface from '../../../graphql/Rooms/ChildSessionInterface';
import { ViewType } from '../../../enums/ContentUrl';
import IframeUrlViewer from '../../ContentUrl/IframeUrlViewer';
import FederatedAssetHubViewer from '../../ContentUrl/FederatedAssetHubViewer';
import { OrganizationFlagEnum } from '../../../lib/features/FeatureFlag';
import useFeatureFlag from '../../../lib/hook/useFeatureFlag';
import { RoomContentWrapperProps } from '../Content/RoomContentWrapper';
import { RoomAssetsProvider } from '../../../lib/context/RoomAssetsContext';
import { ContentSchema } from '../../../definitions/schemas';
import { getConsent } from '../../../lib/Consent';
import { CONSENT } from '../../../enums/RoomConsent';

type UrlViewerProps = Omit<
  FullScreenUrlViewerProps,
  'onFullScreenChange' | 'onUrlClicked' | 'onWindowMessageReceived'
>;

export interface UrlTrackerProps
  extends ContentTrackerProps,
    UrlViewerProps,
    RoomContentWrapperProps {
  /**
   * Optional children to use the tracker as a Higher Order Component that
   * grants the tracking capabilities, instead of relying on its default UI
   */
  children?: React.ReactNode;
  onLoadWithinViewport?: () => void;
  isExpired?: boolean;
  isAssetHubProvider?: boolean;
  isInlineIframeViewer?: boolean;
  width?: number;
  content: Partial<ContentSchema>;
}

const UrlTracker: React.FC<UrlTrackerProps> = (
  props: UrlTrackerProps
): JSX.Element => {
  const { fullScreenContentId, setFullScreenContentId } = useContext(
    FullScreenTrackingContext
  );
  const { isWindowFocused } = useContext(WindowFocusContext);

  const {
    children,
    contactId,
    contentId,
    forceFullScreen,
    iframe,
    roomId,
    track,
    version,
    isAssetHubProvider,
    isInlineIframeViewer,
    width,
    cover,
    rounded,
    disableDefaultCover,
    content,
    ...fullScreenUrlViewerProps
  } = props;

  const { current: tracker } = useRef(new SimpleTracker());
  const isInlineIframeViewerEnabled = useFeatureFlag(
    OrganizationFlagEnum.INLINE_IFRAME_VIEWER
  );

  const isFederatedAssetEnabled = useFeatureFlag(
    OrganizationFlagEnum.FEDERATED_ASSET_ENABLED
  );

  const isConsentDeclinedSuppression = useFeatureFlag(
    OrganizationFlagEnum.CONSENT_DECLINED_SUPPRESSION
  );

  const canRegisterAssetHubChildSession = isConsentDeclinedSuppression
    ? getConsent()?.consent === CONSENT.ACCEPTED
    : true;

  useEffect(() => {
    if (track) {
      tracker.attach((elapsedTime) =>
        trackEvent(TrackEvent.URL_VIEWED_SECONDS, elapsedTime)
      );
    }

    return () => tracker.detach();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * This use effect makes sure to activate tracking when these rules are met:
   *  1. The content has tracking enabled
   *  2. The currently content in full screen IS the URL, which is displaying
   *     an iframe with the embedded site
   *  3. The URL has to be opened in an iframe AND has been forced
   *     to be in full screen
   */
  useEffect(() => {
    const isCurrentlyInFullScreen = fullScreenContentId === contentId;
    const isForcedInFullScreen = forceFullScreen && iframe;

    tracker.setActive(
      !!track &&
        (isForcedInFullScreen || isCurrentlyInFullScreen) &&
        isWindowFocused
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceFullScreen, fullScreenContentId, isWindowFocused]);

  const trackEvent = (
    event: TrackEvent.URL_CLICKED | TrackEvent.URL_VIEWED_SECONDS,
    measure: number
  ) => {
    if (!(track && roomId && version)) {
      return;
    }

    try {
      new TrackInterface(contactId).track({
        contentId,
        event,
        measure,
        metadata: undefined,
        roomId,
        version,
        skip: isAssetHubProvider && !!contactId,
      });
    } catch (e) {
      // user shouldn't be alerted about this fail, but we're catching
      // this exception to avoid "Uncaught promise reject" warnings
    }
  };

  const onFullScreenChange = (isFullScreen: boolean) => {
    setFullScreenContentId(isFullScreen ? contentId : null);
  };

  const onUrlClicked = () => {
    trackEvent(TrackEvent.URL_CLICKED, 1);
  };

  const registerChildSession = (sessionId: string) => {
    try {
      new ChildSessionInterface(props.contactId).registerSession({
        childSessionId: sessionId,
        contentId,
        roomId,
        version,
      });
    } catch (e) {
      // do nothing in case it fails, we do not want to block this user on this.
    }
  };

  const onWindowMessageReceived = (e: MessageEvent<any>) => {
    try {
      const payload = e.data;

      let message = payload;

      if (typeof payload === 'string') {
        try {
          message = JSON.parse(message) as ExternalPayload;
        } catch (e) {
          message = {};
        }
      }

      console.log(
        `Message received for child session registration: ${JSON.stringify(
          payload
        )}`
      );

      switch (message.action) {
        case Actions.AssethubSessionStart: {
          if (
            message.data?.dsrData?.viewType === ViewType.EXTERNAL &&
            message.data?.sessionId &&
            isAssetHubProvider &&
            message.data?.dsrData?.dsrContentId === contentId &&
            canRegisterAssetHubChildSession
          ) {
            return registerChildSession(message.data.sessionId);
          }
        }
      }
    } catch (e) {
      // do nothing
    }
  };

  if (children) {
    return React.cloneElement(children as any, {
      onClick: onUrlClicked,
    });
  }

  if (isInlineIframeViewer && !!isInlineIframeViewerEnabled) {
    return (
      <RoomAssetsProvider>
        {isAssetHubProvider && isFederatedAssetEnabled ? (
          <FederatedAssetHubViewer
            {...(fullScreenUrlViewerProps as any)}
            width={width}
            onWindowMessageReceived={onWindowMessageReceived}
            cover={cover}
            disableDefaultCover={disableDefaultCover}
            rounded={rounded}
            content={content}
            forceFullScreen={forceFullScreen}
          />
        ) : (
          <IframeUrlViewer
            {...fullScreenUrlViewerProps}
            iframe={iframe}
            width={width}
            onWindowMessageReceived={onWindowMessageReceived}
            cover={cover}
            disableDefaultCover={disableDefaultCover}
            rounded={rounded}
            content={content}
            forceFullScreen={forceFullScreen}
          />
        )}
      </RoomAssetsProvider>
    );
  }

  return (
    <FullScreenUrlViewer
      {...fullScreenUrlViewerProps}
      cover={cover}
      forceFullScreen={forceFullScreen}
      iframe={iframe}
      onFullScreenChange={onFullScreenChange}
      onUrlClicked={onUrlClicked}
      track={track}
      onWindowMessageReceived={onWindowMessageReceived}
      content={content}
    />
  );
};

export default UrlTracker;
